function drawPath(renderContext, node, clipPath) {
  const { context } = renderContext;
  const nodeX = node.x;
  const nodeY = node.y;
  const nodeW = node.w;
  const nodeH = node.h;

  context.beginPath();
  // Extract path data from CSS path() function
  const pathData = clipPath.slice(6, -1).trim().toUpperCase();

  // Calculate scaling factors based on node dimensions
  const scaleX = nodeW / 100;
  const scaleY = nodeH / 100;

  // Parse path commands
  const commands = pathData.match(/[MLHVZCSQ][^MLHVZCSQ]*/g) || [];

  let currentX = 0;
  let currentY = 0;

  commands.forEach((command) => {
    const type = command[0];
    const coords = command
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .map(Number);

    switch (type) {
      case 'M': // Move to
        currentX = coords[0] * scaleX;
        currentY = coords[1] * scaleY;
        context.moveTo(nodeX + currentX, nodeY + currentY);
        break;
      case 'L': // Line to
        currentX = coords[0] * scaleX;
        currentY = coords[1] * scaleY;
        context.lineTo(nodeX + currentX, nodeY + currentY);
        break;
      case 'H': // Horizontal line
        currentX = coords[0] * scaleX;
        context.lineTo(nodeX + currentX, nodeY + currentY);
        break;
      case 'V': // Vertical line
        currentY = coords[0] * scaleY;
        context.lineTo(nodeX + currentX, nodeY + currentY);
        break;
      case 'C': {
        // Cubic Bezier curve
        const [x1, y1, x2, y2, x, y] = coords;
        context.bezierCurveTo(
          nodeX + x1 * scaleX,
          nodeY + y1 * scaleY,
          nodeX + x2 * scaleX,
          nodeY + y2 * scaleY,
          nodeX + x * scaleX,
          nodeY + y * scaleY
        );
        currentX = x * scaleX;
        currentY = y * scaleY;
        break;
      }
      case 'S': {
        // Smooth cubic Bezier curve
        const [x2s, y2s, xs, ys] = coords;
        context.bezierCurveTo(
          nodeX + currentX,
          nodeY + currentY,
          nodeX + x2s * scaleX,
          nodeY + y2s * scaleY,
          nodeX + xs * scaleX,
          nodeY + ys * scaleY
        );
        currentX = xs * scaleX;
        currentY = ys * scaleY;
        break;
      }
      case 'Q': {
        // Quadratic Bezier curve
        const [x1q, y1q, xq, yq] = coords;
        context.quadraticCurveTo(
          nodeX + x1q * scaleX,
          nodeY + y1q * scaleY,
          nodeX + xq * scaleX,
          nodeY + yq * scaleY
        );
        currentX = xq * scaleX;
        currentY = yq * scaleY;
        break;
      }
      case 'Z': // Close path
        context.closePath();
        break;
    }
  });
}

// polygon(0px 0px, 100% 0px, 100% 75%, 50% 100%, 0px 75%)
function drawPolygon(renderContext, node, clipPath) {
  const { context } = renderContext;
  const points = clipPath
    .slice(8, -1) // Remove 'polygon(' and ')'
    .split(',')
    .map((point) => point.trim())
    .map((point) => {
      const [x, y] = point.split(' ');
      return {
        x: x.endsWith('%') ? (node.w * parseFloat(x)) / 100 : parseFloat(x),
        y: y.endsWith('%') ? (node.h * parseFloat(y)) / 100 : parseFloat(y),
      };
    });

  context.beginPath();
  context.moveTo(node.x + points[0].x, node.y + points[0].y);

  for (let i = 1; i < points.length; i++) {
    context.lineTo(node.x + points[i].x, node.y + points[i].y);
  }

  context.closePath();
}

// circle(50% at 50% 50%)
function drawCircle(renderContext, node, clipPath) {
  const { context } = renderContext;
  const match = clipPath.match(/circle\((.*?)(?:\s+at\s+(.*?))?\)/);
  if (!match) return;

  const radius = match[1];
  const position = match[2] || '50% 50%';

  const radiusValue = radius.endsWith('%')
    ? (Math.min(node.w, node.h) * parseFloat(radius)) / 100 / 2
    : parseFloat(radius);

  const [posX, posY] = position.split(' ').map((pos) => {
    if (pos.endsWith('%')) {
      return parseFloat(pos) / 100;
    }
    return parseFloat(pos) / Math.min(node.w, node.h);
  });

  const centerX = node.x + node.w * posX;
  const centerY = node.y + node.h * posY;

  context.beginPath();
  context.arc(centerX, centerY, radiusValue, 0, Math.PI * 2);
  context.closePath();
}

// ellipse(50% 50% at 50% 50%)
function drawEllipse(renderContext, node, clipPath) {
  const { context } = renderContext;
  const match = clipPath.match(/ellipse\((.*?)(?:\s+at\s+(.*?))?\)/);
  if (!match) return;

  const [rx, ry] = match[1].split(' ');
  const position = match[2] || '50% 50%';

  const radiusX = rx.endsWith('%') ? (node.w * parseFloat(rx)) / 100 / 2 : parseFloat(rx);
  const radiusY = ry.endsWith('%') ? (node.h * parseFloat(ry)) / 100 / 2 : parseFloat(ry);

  const [posX, posY] = position.split(' ').map((pos, idx) => {
    if (pos.endsWith('%')) {
      return parseFloat(pos) / 100;
    }
    return parseFloat(pos) / (idx === 0 ? node.w : node.h);
  });

  const centerX = node.x + node.w * posX;
  const centerY = node.y + node.h * posY;

  context.beginPath();
  context.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
  context.closePath();
}

// inset(10px 20px 30px 40px round 10px)
function drawInset(renderContext, node, clipPath) {
  const { context } = renderContext;
  const match = clipPath.match(/inset\((.*?)(?:\s+round\s+(.*?))?\)/);
  if (!match) return;

  const [top, right = top, bottom = top, left = right] = match[1].split(' ').map((val) => {
    if (val.endsWith('%')) {
      return (node.h * parseFloat(val)) / 100;
    }
    return parseFloat(val);
  });

  let borderRadius = 0;
  if (match[2]) {
    borderRadius = parseFloat(match[2]);
  }

  const x = node.x + left;
  const y = node.y + top;
  const width = node.w - left - right;
  const height = node.h - top - bottom;

  context.beginPath();
  if (borderRadius > 0) {
    context.moveTo(x + borderRadius, y);
    context.arcTo(x + width, y, x + width, y + height, borderRadius);
    context.arcTo(x + width, y + height, x, y + height, borderRadius);
    context.arcTo(x, y + height, x, y, borderRadius);
    context.arcTo(x, y, x + width, y, borderRadius);
  } else {
    context.rect(x, y, width, height);
  }
  context.closePath();
}

function drawXYWH(renderContext, node, clipPath) {
  const { context } = renderContext;
  const match = clipPath.match(/xywh\((.*?)(?:\s+round\s+(.*?))?\)/);
  if (!match) return;

  const nodeX = node.x;
  const nodeY = node.y;
  const nodeW = node.w;
  const nodeH = node.h;

  const [x, y, width, height] = match[1].split(/\s+/).map((val, idx) => {
    if (val.endsWith('%')) {
      const percent = parseFloat(val) / 100;
      return idx === 0 || idx === 2 ? nodeW * percent : nodeH * percent;
    }
    if (val.endsWith('px')) {
      return parseFloat(val);
    }
    return parseFloat(val);
  });

  let borderRadius = { topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0 };

  if (match[2]) {
    const [radius1 = '0', radius2 = radius1] = match[2].split(/\s+/);
    const r1 = (parseFloat(radius1) / 100) * Math.min(width, height);
    const r2 = (parseFloat(radius2) / 100) * Math.min(width, height);
    borderRadius = {
      topLeft: r1,
      topRight: r2,
      bottomRight: r1,
      bottomLeft: r2,
    };
  }

  context.beginPath();
  context.moveTo(nodeX + x + borderRadius.topLeft, nodeY + y);
  context.lineTo(nodeX + x + width - borderRadius.topRight, nodeY + y);
  context.arcTo(
    nodeX + x + width,
    nodeY + y,
    nodeX + x + width,
    nodeY + y + borderRadius.topRight,
    borderRadius.topRight
  );
  context.lineTo(nodeX + x + width, nodeY + y + height - borderRadius.bottomRight);
  context.arcTo(
    nodeX + x + width,
    nodeY + y + height,
    nodeX + x + width - borderRadius.bottomRight,
    nodeY + y + height,
    borderRadius.bottomRight
  );
  context.lineTo(nodeX + x + borderRadius.bottomLeft, nodeY + y + height);
  context.arcTo(
    nodeX + x,
    nodeY + y + height,
    nodeX + x,
    nodeY + y + height - borderRadius.bottomLeft,
    borderRadius.bottomLeft
  );
  context.lineTo(nodeX + x, nodeY + y + borderRadius.topLeft);
  context.arcTo(
    nodeX + x,
    nodeY + y,
    nodeX + x + borderRadius.topLeft,
    nodeY + y,
    borderRadius.topLeft
  );
  context.closePath();
}

export function drawClipPath(renderContext, node, render) {
  const { context } = renderContext;
  const clipPath = node.clipPath;
  if (clipPath) {
    context.save();
    try {
      if (clipPath.startsWith('path(')) {
        drawPath(renderContext, node, clipPath);
        context.clip();
      } else if (clipPath.startsWith('polygon(')) {
        drawPolygon(renderContext, node, clipPath);
        context.clip();
      } else if (clipPath.startsWith('circle(')) {
        drawCircle(renderContext, node, clipPath);
        context.clip();
      } else if (clipPath.startsWith('ellipse(')) {
        drawEllipse(renderContext, node, clipPath);
        context.clip();
      } else if (clipPath.startsWith('inset(')) {
        drawInset(renderContext, node, clipPath);
        context.clip();
      } else if (clipPath.startsWith('xywh(')) {
        drawXYWH(renderContext, node, clipPath);
        context.clip();
      }
    } catch {}
    render();
    context.restore();
  } else {
    render();
  }
}
